import React, { useContext, createContext, useCallback, useMemo, useState, useEffect } from 'react';
import {
  // locales
  getDefaultLocale,
  LOCALE_HY,
  LOCALE_EN,
  LOCALE_RU,
  LOCALE_UZ,
  locales as availableLocales,
  localeNames,
  // data
  translate as nativeTranslate,
  mergeTranslationsMap,
  translations,
  loadTranslations,
  setMomentLocale,
} from '@happylife-a/localization';
import * as storage from '../helpers/storage';

const CURRENT_LOCALE_STORAGE_KEY = 'TranslationContext@currentLocale';
async function setCurrentLocaleToStorage(locale) {
  return storage.setItem(CURRENT_LOCALE_STORAGE_KEY, locale);
}

export function getCurrentLocaleFromStorage() {
  return storage.getItem(CURRENT_LOCALE_STORAGE_KEY);
}

export { LOCALE_HY, LOCALE_EN, LOCALE_RU, LOCALE_UZ };

export const TranslationContext = createContext({
  t: () => '',
  tWithLocale: () => '',
  locale: '',
  available: availableLocales,
  localeNames: {},
  onLocaleChange: () => {},
});

export function useTranslation() {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('Please use hook inside of TranslationProvider.');
  }

  return context;
}

export function TranslationProvider({
  children,
  translations: providedTranslations,
  locale: initialLocale = getDefaultLocale(),
  onLocaleChange = () => {},
}) {
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    loadTranslations()
      .then(() => setIsLoading(false))
      .catch((e) => {
        console.error('Failed to load translations:', e);
      });
  }, []);

  const [locale, setLocale] = useState(initialLocale);
  const mergedTranslations = useMemo(() => {
    return availableLocales.reduce((acc, loopLocale) => {
      acc[loopLocale] = {
        ...providedTranslations[loopLocale],
        ...translations[loopLocale],
      };
      return acc;
    }, {});
  }, [isLoading, providedTranslations]);

  useEffect(() => {
    mergeTranslationsMap(mergedTranslations);
    setMomentLocale(locale);
  }, [mergedTranslations]);

  useEffect(() => {
    setCurrentLocaleToStorage(locale);
  }, [locale]);

  const t = useCallback(
    (text, data = {}) => nativeTranslate(null, locale, text, data),
    [locale, mergedTranslations]
  );

  const tWithLocale = useCallback(
    (text, customLocale, data = {}) => nativeTranslate(null, customLocale, text, data),
    [mergedTranslations]
  );

  const onChange = useCallback(
    (newLocale) => {
      setLocale(newLocale);
      setMomentLocale(newLocale);
      if (typeof onLocaleChange === 'function') {
        onLocaleChange(newLocale);
      }
    },
    [onLocaleChange]
  );

  if (isLoading) {
    return null;
  }

  return (
    <TranslationContext.Provider
      value={{
        available: availableLocales,
        locale: locale,
        defaultLocale: getDefaultLocale(),
        localeNames: localeNames,
        onLocaleChange: onChange,
        t: t,
        tWithLocale: tWithLocale,
      }}
    >
      {children}
    </TranslationContext.Provider>
  );
}
